import './styles.css';
import { Root, Content } from '@radix-ui/react-collapsible';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import React, { useEffect, useState } from 'react';
import { Control, Controller, UseFormTrigger, UseFormWatch } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import { WorkflowActionFieldName } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowEscalatedList/WorkflowActionFields/WorkflowActionFieldName';
import { WorkflowActionFieldEmail } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowEscalatedList/WorkflowActionFields/WorkflowActionFieldEmail';
import { WorkflowActionFieldPhone } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowEscalatedList/WorkflowActionFields/WorkflowActionFieldPhone';
import { WorkflowActionFieldCustom } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowEscalatedList/WorkflowActionFields/WorkflowActionFieldCustom';
import { WorkflowActionFieldCustomArray } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowEscalatedList/WorkflowActionFields/WorkflowActionFieldCustomArray';
import { clsx } from 'clsx';
import type {
  WorkflowActionPath,
  WorkflowFormValues,
} from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';

export const WorkflowEscalatedListItem = ({
  control,
  label,
  description,
  value,
  isExpanded,
  watch,
  trigger = () => Promise.resolve(true),
  namePrefix = 'action.0',
  isCustom = true,
}: {
  control: Control<WorkflowFormValues>;
  label: string;
  description?: React.ReactNode;
  value: string;
  isExpanded: boolean;
  watch: UseFormWatch<WorkflowFormValues>;
  trigger?: UseFormTrigger<WorkflowFormValues>;
  namePrefix?: WorkflowActionPath;
  isCustom?: boolean;
}) => {
  const idPrefix = namePrefix.split('.')[1];
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(isExpanded);
  }, [isExpanded]);

  return (
    <Root
      className={clsx(
        'group px-2.5 hover:border-[#a498ff54] hover:bg-[#a498ff0d] transition rounded-sm-5 [&[data-state=open]]:bg-[#a498ff1a] border [&[data-state=open]]:border-[#a498ff80]',
        isCustom ? 'border-transparent' : 'border-[#E3E6E8]',
      )}
      open={open}
      onOpenChange={setOpen}
    >
      <div className="flex items-center space-x-[7px] space-y-0 w-full h-full">
        <label
          htmlFor={`${idPrefix}radio-item-${value}`}
          className="py-2.5 cursor-pointer w-full h-full flex flex-row gap-3 items-center"
        >
          <RadioGroupItem
            // Radix leaves `name` out of the item's props; it is still set on the rendered button.
            {...({ name: 'action_escalated_list' } as object)}
            value={value}
            id={`${idPrefix}radio-item-${value}`}
          />
          <div>
            <p
              className={clsx(
                'font-medium text-ebony opacity-50 group-hover:opacity-100 group-[&[data-state=open]]:opacity-100 transition',
                isCustom ? 'text-sm' : 'text-base',
              )}
            >
              {label}
            </p>
            {!isCustom && description && (
              <p className="font-medium text-sm text-ebony_opacity_50 opacity-50 group-hover:opacity-100 group-[&[data-state=open]]:opacity-100 transition">
                {description}
              </p>
            )}
          </div>
        </label>
      </div>
      {['collect_and_escalate', 'forward_to_email'].includes(value) && (
        <Content className={'CollapsibleContent'}>
          <div className={'flex flex-col w-full h-full mb-2.5 gap-5'}>
            {value === 'forward_to_email' && (
              <div className={'flex flex-col w-full gap-[5px] px-2'}>
                <Controller
                  control={control}
                  name={`${namePrefix}.receiver_email_addresses`}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      type={'textarea'}
                      label={'Forward to*:'}
                      onChange={onChange}
                      value={value}
                      isValid={!error?.message}
                      invalidMessage={error?.message}
                      textareaRows={2}
                      className={'!text-sm'}
                      placeholder={'email1@example.com, email2@example.com, email3@example.com'}
                    />
                  )}
                />
              </div>
            )}
            <div className={'flex flex-col w-full gap-[5px] px-2'}>
              <Controller
                control={control}
                name={`${namePrefix}.message_before`}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'AI message before data collection'}
                    placeholder={'Message text'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    className={'!py-2 !px-3.5'}
                  />
                )}
              />
            </div>
            <div className={'flex flex-col w-full gap-2.5 px-2'}>
              <div>
                <p className={'font-medium text-sm text-ebony'}>Data collection*</p>
              </div>
              <div className={'flex flex-row gap-5 justify-around w-full'}>
                <WorkflowActionFieldName
                  namePrefix={namePrefix}
                  control={control}
                  trigger={trigger}
                />
                <WorkflowActionFieldEmail
                  namePrefix={namePrefix}
                  control={control}
                  trigger={trigger}
                />
                <WorkflowActionFieldPhone
                  namePrefix={namePrefix}
                  control={control}
                  trigger={trigger}
                />
                <WorkflowActionFieldCustom
                  namePrefix={namePrefix}
                  control={control}
                  trigger={trigger}
                />
              </div>
            </div>
            <WorkflowActionFieldCustomArray
              namePrefix={namePrefix}
              control={control}
              watch={watch}
            />
            <div className={'flex flex-col w-full gap-[5px] px-2'}>
              <Controller
                control={control}
                name={`${namePrefix}.message_after`}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'AI message after data collection'}
                    placeholder={'Message text'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    className={'!py-2 !px-3.5'}
                  />
                )}
              />
            </div>
            {value === 'forward_to_email' && (
              <div className={'flex flex-col w-full gap-2.5 px-2'}>
                <div>
                  <p className={'font-medium text-sm text-ebony'}>
                    Send conversation summary or entire transcript?
                  </p>
                </div>
                <Controller
                  control={control}
                  name={`${namePrefix}.action_message_type`}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <RadioGroup
                      onValueChange={onChange}
                      defaultValue={value || 'transcript'}
                      className="flex flex-row gap-5"
                    >
                      <label
                        htmlFor={`radio-item-transcript`}
                        className="py-2.5 cursor-pointer flex flex-row gap-3 items-center"
                      >
                        <RadioGroupItem value={'transcript'} id={`radio-item-transcript`} />
                        <p className="font-medium text-sm text-ebony_opacity_50 opacity-50 group-hover:opacity-100 group-[&[data-state=open]]:opacity-100 transition">
                          Entire transcript
                        </p>
                      </label>
                      <label
                        htmlFor={`radio-item-summary`}
                        className="py-2.5 cursor-pointer flex flex-row gap-3 items-center"
                      >
                        <RadioGroupItem value={'summary'} id={`radio-item-summary`} />
                        <p className="font-medium text-sm text-ebony_opacity_50 opacity-50 group-hover:opacity-100 group-[&[data-state=open]]:opacity-100 transition">
                          Summary
                        </p>
                      </label>
                    </RadioGroup>
                  )}
                />
              </div>
            )}
          </div>
        </Content>
      )}
      {'close' === value && (
        <Content className={'CollapsibleContent'}>
          <div className={'flex flex-col w-full h-full mb-2.5 gap-5'}>
            <div className={'flex flex-col w-full gap-[5px] px-2'}>
              <Controller
                control={control}
                name={`${namePrefix}.message_close`}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'Closing message'}
                    placeholder={'To get help, submit the ticket here OR email us at…'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    className={'!py-2 !px-3.5'}
                  />
                )}
              />
            </div>
          </div>
        </Content>
      )}
    </Root>
  );
};
